import { Injectable } from '@angular/core';
import { Router } from '@angular/router';
import { Observable, of } from 'rxjs';
import { map, catchError } from 'rxjs/operators';
import { AuthService } from './auth.service';
import { GlobalStateService } from '../global/global-state.service';

@Injectable({
  providedIn: 'root'
})
export class AuthInitializationService {

  private isInitialized = false;

  constructor(
    private authService: AuthService,
    private globalStateService: GlobalStateService,
    private router: Router
  ) {}

  /**
   * Initialize authentication state on app startup
   * This should be called once when the app starts
   */
  public initializeAuth(): Observable<boolean> {
    if (this.isInitialized) {
      return of(true);
    }

    // Load countries data first (available for both authenticated and unauthenticated users)
    this.globalStateService.SetCountries();
    this.globalStateService.SetSetting();

    // Check if user has auth token
    if (!this.authService.hasAuthToken()) {
      // No token, user is not authenticated
      this.handleUnauthenticatedState();
      this.isInitialized = true;
      return of(false);
    }

    // Validate token by calling GetProfile
    return this.authService.validateAuthentication().pipe(
      map(isAuthenticated => {
        if (isAuthenticated) {
          this.handleAuthenticatedState();
        } else {
          this.handleUnauthenticatedState();
        }
        this.isInitialized = true;
        return isAuthenticated;
      }),
      catchError(error => {
        console.error('Auth initialization failed:', error);
        this.handleUnauthenticatedState();
        this.isInitialized = true;
        return of(false);
      })
    );
  }

  /**
   * Handle authenticated user state
   */
  private handleAuthenticatedState(): void {
    // User is authenticated, check current route
    const currentUrl = this.router.url;
    const authRoutes = ['/login', '/register', '/reset', '/2fa'];

    // If user is on auth page, redirect to dashboard
    if (authRoutes.includes(currentUrl)) {
      this.router.navigate(['/dashboard']);
    }
  }

  /**
   * Handle unauthenticated user state
   */
  private handleUnauthenticatedState(): void {
    // Clear any existing auth state
    this.authService.clearAuthState();

    // Check current route
    const currentUrl = this.router.url;
    const queryParams = this.router.parseUrl(currentUrl).queryParams;
    const authRoutes = ['/login', '/register', '/reset', '/2fa', '/'];

    // If user is not on auth page, redirect to login
    if (!authRoutes.includes(currentUrl)) {
      this.router.navigate(['/login']);
    }
  }

  /**
   * Check if initialization is complete
   */
  public isAuthInitialized(): boolean {
    return this.isInitialized;
  }

  /**
   * Reset initialization state (useful for testing)
   */
  public resetInitialization(): void {
    this.isInitialized = false;
  }
}
